import { Grid, Rating, Box, IconButton, useMediaQuery, Typography } from "@mui/material";
import { Fragment, useState, useEffect, useMemo } from "react";
import LocalStorageService from "../../services/LocalStorageService";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import Swal from "sweetalert2";

// Icons
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PrintIcon from '@mui/icons-material/Print';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TimerIcon from '@mui/icons-material/Timer';
import MonitorIcon from '@mui/icons-material/Monitor';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

// API & Components
import { getServiceDescription, getServiceCommentsAndRating } from "../../api/ServiceDescription";
import { getAllServices } from "../../api/ListOfServicesPerCategory";
import Comment from "./components/Comment/Comment";
import CenterLoading from "../../components/CenterLoading/CenterLoading";
import LoginOrRegisterModal from "../../components/LoginOrRegisterModal/LoginOrRegisterModal";
import ContactFormModal from "./components/ContactFormModal/ContactFormModal";
import QRCodeModal from "./components/QRCodeModal/QRCodeModal";
import { ROLE_TYPE } from "../../constants/constants";
import { ShowGlobalLoading, HideGlobalLoading, UpdateAppSubHeaderTitle } from "../../redux/actions/UiActions";
import COLORS from "../../theme/Colors";

// Styles
import * as S from "./styles/ServiceDescriptionStyles";
import { hourIn24To12hours, priceVariationToLaborableTime } from "../../utilities/functions/FormatterUtil";
import { compareBySlug, generateSlug } from "../../utils/slugify";

function ServiceDescription() {
    const { slug } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const matchesWidth = useMediaQuery("(min-width:1024px)");
    const { authenticated, rolBase } = useSelector((state) => state.authReducer);

    const [activeTab, setActiveTab] = useState('general');
    const [loginOrRegisterModalStatus, setLoginOrRegisterModalStatus] = useState(false);
    const [contactFormModalOpen, setContactFormModalOpen] = useState(false);
    const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);

    const { data: allCategories, isLoading: isAllCategoriesLoading } = useQuery("allServices", getAllServices);

    const resolvedServiceID = useMemo(() => {
        if (!allCategories || !slug) return null;
        return allCategories
            .flatMap((cat) => cat.services || [])
            .find((s) => compareBySlug(s.name, slug) || s.id.toString() === slug)?.id || null;
    }, [allCategories, slug]);

    const { data: serviceDescription, isLoading } = useQuery(
        ["serviceDescription", resolvedServiceID], 
        async () => {
            dispatch(ShowGlobalLoading("Cargando"));
            const response = await getServiceDescription(resolvedServiceID);
            dispatch(HideGlobalLoading());
            return response;
        },
        {
            enabled: !!resolvedServiceID,
            onError: () => {
                dispatch(HideGlobalLoading());
                history.push("/");
            }
        }
    );
    
    const { data: extrasData } = useQuery(["serviceExtras", resolvedServiceID], () => getServiceCommentsAndRating(resolvedServiceID), {
        enabled: !!resolvedServiceID
    });

    const relatedCategory = allCategories?.find(cat => 
        cat.services?.some(s => s.id === resolvedServiceID)
    );

    const relatedServices = relatedCategory?.services
        ?.filter(s => s.id !== resolvedServiceID)
        ?.slice(0, 3) || [];

    const handleShare = (platform) => {
        const url = window.location.href;
        const text = `Consulta este servicio en UCTT: ${serviceDescription?.name}`;
        
        switch (platform) {
            case 'facebook':
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                break;
            case 'twitter':
                window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
                break;
            case 'whatsapp':
                window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, '_blank');
                break;
            case 'telegram':
                window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
                break;
            case 'linkedin':
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
                break;
            case 'email':
                window.location.href = `mailto:?subject=${encodeURIComponent(serviceDescription?.name)}&body=${encodeURIComponent(text + "\n\n" + url)}`;
                break;
            case 'print':
                window.print();
                break;
            default:
                break;
        }
    };

    // Removed UpdateAppSubHeaderTitle to eliminate the blue header section
    useEffect(() => {
        dispatch(UpdateAppSubHeaderTitle(""));
    }, [dispatch]);

    if (isAllCategoriesLoading || isLoading) return <CenterLoading />;
    if (!resolvedServiceID && !isAllCategoriesLoading) {
        history.push("/todos-los-tramites");
        return null;
    }

    const handleOpenModal = () => {
        if (rolBase === ROLE_TYPE.COMPANY) {
            Swal.fire({
                title: "Información",
                text: "No puede realizar una solicitud directamente, debe ser a través de un representante",
                icon: "warning",
            });
            return;
        }
        if (authenticated) {
            history.push(`/solicitar-tramite/${slug}`);
        } else {
            LocalStorageService.setItem("redirect_after_login", `/solicitar-tramite/${slug}`);
            setLoginOrRegisterModalStatus(true);
        }
    };

    // Data Extraction Helpers
    const mainSchedule = serviceDescription?.schedules?.[0] 
        ? `${serviceDescription.schedules[0].day}: ${hourIn24To12hours(serviceDescription.schedules[0].init)} - ${hourIn24To12hours(serviceDescription.schedules[0].finit)}`
        : "Lunes a Viernes: 8:00 am - 4:00 pm";

    const mainPriceVariation = serviceDescription?.prices?.[0]?.variations?.[0];
    const formattedPrice = mainPriceVariation 
        ? new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }).format(mainPriceVariation.price)
        : "Gratis / Variable";
    
    const responseTime = mainPriceVariation?.delivery_time 
        ? priceVariationToLaborableTime(mainPriceVariation.delivery_time)
        : "Desde 24 horas laborables";

    // Calculate average rating from comments if not explicitly provided by API
    const averageRating = extrasData?.data?.length > 0
        ? extrasData.data.reduce((acc, curr) => acc + curr.rating, 0) / extrasData.data.length
        : 0;

    return (
        <div style={{ width: '100%', display: 'block' }}>
            <S.BreadcrumbWrapper>
                <S.BreadcrumbInner>
                    <S.BreadcrumbContent>
                        <S.BreadcrumbLink onClick={() => history.push('/')}>Inicio</S.BreadcrumbLink>
                        <S.BreadcrumbSeparator>/</S.BreadcrumbSeparator>
                        <S.BreadcrumbLink onClick={() => history.push(`/todos-los-tramites?categoria=${generateSlug(relatedCategory?.name || "")}`)}>Servicios</S.BreadcrumbLink>
                        <S.BreadcrumbSeparator>/</S.BreadcrumbSeparator>
                        <S.BreadcrumbCurrent>{serviceDescription?.name}</S.BreadcrumbCurrent>
                    </S.BreadcrumbContent>
                </S.BreadcrumbInner>
            </S.BreadcrumbWrapper>

            <S.PageWrapper>
            <LoginOrRegisterModal
                open={loginOrRegisterModalStatus}
                onBackDropClick={() => setLoginOrRegisterModalStatus(false)}
                onCloseClick={() => setLoginOrRegisterModalStatus(false)}
            />

            <S.MainGrid container spacing={4}>
                {/* Main Column */}
                <Grid item xs={12} lg={8}>
                    <Box mb={2} display="flex" alignItems="center" sx={{ height: '42px' }}>
                        <S.SocialShareGrid>
                            <S.SocialShareIcon onClick={() => handleShare('email')} title="Enviar por Email">
                                <EmailIcon />
                            </S.SocialShareIcon>
                            <S.SocialShareIcon onClick={() => handleShare('print')} title="Imprimir">
                                <PrintIcon />
                            </S.SocialShareIcon>
                            <S.SocialShareIcon onClick={() => handleShare('facebook')} title="Compartir en Facebook">
                                <FacebookIcon />
                            </S.SocialShareIcon>
                            <S.SocialShareIcon onClick={() => handleShare('twitter')} title="Compartir en X">
                                <TwitterIcon />
                            </S.SocialShareIcon>
                            <S.SocialShareIcon onClick={() => handleShare('whatsapp')} title="Compartir en WhatsApp">
                                <WhatsAppIcon />
                            </S.SocialShareIcon>
                            <S.SocialShareIcon onClick={() => handleShare('telegram')} title="Compartir en Telegram">
                                <TelegramIcon />
                            </S.SocialShareIcon>
                            <S.SocialShareIcon title="Instagram">
                                <InstagramIcon />
                            </S.SocialShareIcon>
                            <S.SocialShareIcon onClick={() => handleShare('linkedin')} title="Compartir en LinkedIn">
                                <LinkedInIcon />
                            </S.SocialShareIcon>
                        </S.SocialShareGrid>
                    </Box>

                    <S.ServiceCard>
                        <S.ServiceTitle>{serviceDescription?.name}</S.ServiceTitle>



                        <S.TabsWrapper>
                            <S.TabItem active={activeTab === 'general'} onClick={() => setActiveTab('general')}>Información General</S.TabItem>
                            <S.TabItem active={activeTab === 'requisitos'} onClick={() => setActiveTab('requisitos')}>Requisitos</S.TabItem>
                            <S.TabItem active={activeTab === 'procedimiento'} onClick={() => setActiveTab('procedimiento')}>Procedimiento</S.TabItem>
                        </S.TabsWrapper>

                        {activeTab === 'general' && (
                            <Box>
                                <S.SectionHeader>Descripción del Servicio</S.SectionHeader>
                                <S.DescriptionText>{serviceDescription?.description}</S.DescriptionText>

                                <S.SectionHeader>Dirigido a</S.SectionHeader>
                                <S.DescriptionText>
                                    {/* MOCKED: API does not provide target_audience yet */}
                                    Personas naturales o jurídicas que requieran operar servicios turísticos correspondientes al sector en la República Dominicana.
                                </S.DescriptionText>

                                <S.InfoBlockContainer container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <S.InfoItem>
                                            <S.InfoIconWrapper><AccessTimeIcon /></S.InfoIconWrapper>
                                            <S.InfoTextWrapper>
                                                <S.InfoLabel>Horario de Prestación</S.InfoLabel>
                                                <S.InfoValue>{mainSchedule}</S.InfoValue>
                                            </S.InfoTextWrapper>
                                        </S.InfoItem>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <S.InfoItem>
                                            <S.InfoIconWrapper><AttachMoneyIcon /></S.InfoIconWrapper>
                                            <S.InfoTextWrapper>
                                                <S.InfoLabel>Costo</S.InfoLabel>
                                                <S.InfoValue>{formattedPrice}</S.InfoValue>
                                            </S.InfoTextWrapper>
                                        </S.InfoItem>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <S.InfoItem>
                                            <S.InfoIconWrapper><TimerIcon /></S.InfoIconWrapper>
                                            <S.InfoTextWrapper>
                                                <S.InfoLabel>Tiempo de Respuesta</S.InfoLabel>
                                                <S.InfoValue>{responseTime}</S.InfoValue>
                                            </S.InfoTextWrapper>
                                        </S.InfoItem>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <S.InfoItem>
                                            <S.InfoIconWrapper><MonitorIcon /></S.InfoIconWrapper>
                                            <S.InfoTextWrapper>
                                                <S.InfoLabel>Canal de Prestación</S.InfoLabel>
                                                <S.InfoValue>
                                                    {serviceDescription?.type?.id === 1 ? "Informativo" : "Sistema en línea"}
                                                </S.InfoValue>
                                            </S.InfoTextWrapper>
                                        </S.InfoItem>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <S.InfoItem>
                                            <S.InfoIconWrapper><EventIcon /></S.InfoIconWrapper>
                                            <S.InfoTextWrapper>
                                                <S.InfoLabel>Tiempo de Realización</S.InfoLabel>
                                                {/* MOCKED: API does not provide a fixed realization_time for the whole service */}
                                                <S.InfoValue>45 Días laborables</S.InfoValue>
                                            </S.InfoTextWrapper>
                                        </S.InfoItem>
                                    </Grid>
                                </S.InfoBlockContainer>

                                <S.NoteBox>
                                    Las solicitudes pueden realizarse las 24 horas los 365 días del año a través de nuestro sistema en línea, sin embargo, el procesamiento y atención se realizará únicamente en horario laboral: de lunes a viernes de 8:00 am a 4:00 pm, excluyendo días feriados oficiales.
                                </S.NoteBox>
                            </Box>
                        )}

                        {activeTab === 'requisitos' && (
                            <Box pt={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <S.SectionHeader style={{ marginBottom: 0 }}>Requisitos para {serviceDescription?.name}</S.SectionHeader>
                                    <S.DownloadButton onClick={() => window.print()}>
                                        <PictureAsPdfIcon /> Descargar PDF
                                    </S.DownloadButton>
                                </Box>
                                <S.DescriptionText>Los siguientes requisitos son necesarios para completar su solicitud:</S.DescriptionText>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {serviceDescription?.requirements?.map((req, idx) => (
                                        <li key={idx} style={{ marginBottom: '1.2rem', paddingLeft: '1.5rem', position: 'relative' }}>
                                            <span style={{ position: 'absolute', left: 0, color: COLORS.primary }}>•</span>
                                            <S.InfoLabel style={{ fontSize: '16px' }}>{req.name}</S.InfoLabel>
                                            {req.route && (
                                                <a href={req.route} target="_blank" rel="noreferrer" style={{ display: 'block', fontSize: '13px', color: COLORS.primary, marginBottom: '4px' }}>
                                                    Descargar formulario/plantilla
                                                </a>
                                            )}
                                            <S.DescriptionText style={{ marginTop: '4px' }}>{req.description}</S.DescriptionText>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}

                        {activeTab === 'procedimiento' && (
                            <Box pt={2}>
                                <S.SectionHeader>Procedimiento</S.SectionHeader>
                                <S.DescriptionText>Pasos a seguir para la realización del trámite:</S.DescriptionText>
                                <ol style={{ paddingLeft: '1.2rem' }}>
                                    {serviceDescription?.procedures?.map((proc, idx) => (
                                        <li key={idx} style={{ marginBottom: '1rem' }}>
                                            <S.DescriptionText style={{ margin: 0 }}>{proc.step}</S.DescriptionText>
                                        </li>
                                    ))}
                                </ol>
                            </Box>
                        )}

                        <S.ActionButton onClick={handleOpenModal}>
                            Solicitar este servicio <ArrowForwardIcon style={{ marginLeft: '8px', fontSize: '18px' }} />
                        </S.ActionButton>
                    </S.ServiceCard>
                    
                    <S.BackButton onClick={() => history.push('/todos-los-tramites')}>
                        <ArrowBackIcon style={{ marginRight: '8px', fontSize: '16px' }} />
                        Volver a todos los servicios
                    </S.BackButton>

                    <Box mt={6} pt={4} style={{ borderTop: '1px solid #E2E8F0' }}>
                        <S.SectionHeader>Opiniones de usuarios</S.SectionHeader>
                        <Box mb={3} display="flex" alignItems="center" gap={2}>
                            <Rating value={averageRating} precision={0.5} readOnly size="large" />
                            <Typography variant="h6" color="text.secondary">
                                {averageRating.toFixed(1)} / 5.0
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {extrasData?.data?.length > 0 ? (
                                extrasData.data.map((comment, idx) => (
                                    <Comment 
                                        key={idx}
                                        userName={`${comment.citizen?.name || ""} ${comment.citizen?.surname || ""}`.trim() || "Usuario UCTT"}
                                        date={comment.created_at}
                                        comment={comment.comment}
                                        rating={comment.rating}
                                    />
                                ))
                            ) : (
                                <S.DescriptionText>No hay comentarios para este servicio todavía.</S.DescriptionText>
                            )}
                        </Box>
                    </Box>
                </Grid>

                {/* Sidebar Column */}
                <Grid item xs={12} lg={4}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ height: '42px', gap: '15px' }}>
                        <Rating 
                            value={averageRating} 
                            precision={0.5} 
                            readOnly 
                            size="medium"
                            sx={{ color: '#FFD700' }}
                        />
                        <S.ContactButton
                            style={{
                                width: 'auto',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            onClick={() => setQrCodeModalOpen(true)}
                        >
                            <QrCodeIcon style={{ marginRight: '8px', fontSize: '18px' }} />
                            Código QR
                        </S.ContactButton>
                    </Box>
                    <S.SidebarCard>
                        <S.SidebarTitle>Información de Contacto</S.SidebarTitle>
                        
                        <S.ContactItem>
                            <ApartmentIcon />
                            <Box>
                                <S.ContactLabel>Departamento que ofrece el servicio</S.ContactLabel>
                                <S.ContactValue>{serviceDescription?.department?.name || "Dirección de Empresas y Servicios"}</S.ContactValue>
                            </Box>
                        </S.ContactItem>

                        <S.ContactItem>
                            <LocationOnIcon />
                            <Box>
                                <S.ContactLabel>Dirección</S.ContactLabel>
                                <S.ContactValue>
                                    {/* MOCKED: API department object doesn't include physical address/meta */}
                                    Av. México esq. 30 de Marzo, edificio Oficinas Gubernamentales Presidente Prof. Juan Bosch Gaviño, Bloque D, primer piso, Gascue, Santo Domingo, D. N.
                                </S.ContactValue>
                            </Box>
                        </S.ContactItem>

                        <S.ContactItem>
                            <PhoneIcon />
                            <Box>
                                <S.ContactLabel>Teléfono</S.ContactLabel>
                                <S.ContactValue>(809)-638-7804 ext.: 1033</S.ContactValue>
                            </Box>
                        </S.ContactItem>

                        <S.ContactItem>
                            <EmailIcon />
                            <Box>
                                <S.ContactLabel>Email</S.ContactLabel>
                                <S.ContactValue style={{ color: COLORS.primary, fontWeight: 600 }}>
                                    servicioalcliente-mitur@mitur.gob.do
                                </S.ContactValue>
                            </Box>
                        </S.ContactItem>

                        <S.ContactButton onClick={() => setContactFormModalOpen(true)}>Formulario de contacto</S.ContactButton>
                    </S.SidebarCard>

                    <Box mt={4}>
                        <S.SidebarTitle style={{ paddingLeft: '0.5rem' }}>
                            Otros servicios en {relatedCategory?.name || "esta categoría"}
                        </S.SidebarTitle>
                        
                        {relatedServices.length > 0 ? (
                            relatedServices.map((service) => (
                                <S.RelatedServiceItem key={service.id} onClick={() => history.push(`/tramite/${generateSlug(service.name)}`)}>
                                    <S.RelatedTitle>{service.name}</S.RelatedTitle>
                                    <S.RelatedDescription>
                                        {service.description || "Sin descripción disponible."}
                                    </S.RelatedDescription>
                                    <S.RelatedLink>Ver servicio <ArrowForwardIcon style={{ fontSize: '14px' }} /></S.RelatedLink>
                                </S.RelatedServiceItem>
                            ))
                        ) : (
                            <Box p={2}>
                                <Typography variant="body2" color="text.secondary">
                                    No hay más servicios en esta categoría.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </S.MainGrid>
            <ContactFormModal 
                open={contactFormModalOpen} 
                onClose={() => setContactFormModalOpen(false)}
                serviceName={serviceDescription?.name}
            />
            <QRCodeModal 
                open={qrCodeModalOpen}
                onClose={() => setQrCodeModalOpen(false)}
                url={window.location.href}
                serviceName={serviceDescription?.name}
            />
        </S.PageWrapper>
        </div>
    );
}

export default ServiceDescription;
